import React from "react";
import { useLocation, useNavigate } from "react-router";
import { IconoirArrowLeft, IconoirRefresh } from "@ey-xd/motif-icon";
import { MotifIcon } from "@ey-xd/motif-react";

import confusedImg from "../../assets/images/confused.svg";
import fileNotFoundImg from "../../assets/images/file_not_found.svg";
import searchNotFoundImg from "../../assets/images/not_found_error.svg";

import { InternalErrorCode } from "../../utils/apiStatus.enum";
import "./HttpError.scss";

type ErrorState = {
  status?: number;
  title?: string;
  message?: string;
  from?: string;
  time?: number;
};

function titleForStatus(status?: number) {
  switch (status) {
    case 401:
      return "Error 401: Unauthorized";
    case 403:
      return "Error 403: Forbidden";
    case 404:
      return "Error 404: Not Found";
    case 408:
      return "Error 408: Request Timeout";
    case 409:
      return "Error 409: Conflict";
    case 500:
      return "Error 500: Internal Server Error";
    case 502:
      return "Error 502: Bad Gateway";
    case 504:
      return "Error 504: Gateway Timeout";
    default:
      return "Error 404: Not Found";
  }
}

function imageForStatus(status?: number): string {
  switch (status) {
    case 401:
    case 403:
    case 500:
    case 502:
      return confusedImg ?? "";
    case 408:
    case 409:
    case 504:
      return fileNotFoundImg ?? "";
    case 400:
    case 404:
      return searchNotFoundImg ?? "";
    default:
      return confusedImg ?? "";
  }
}

const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const err = (location.state as ErrorState) || {};

  const status = err?.status;
  const heading = err?.title ?? titleForStatus(status);
  const imgSrc = imageForStatus(status);
  const message =
    err?.message ??
    (status === 401
      ? "Authenticate yourself and try again."
      : status === 403
        ? InternalErrorCode.forbidden
        : InternalErrorCode.NotFound);

  const safeImgSrc = imgSrc || confusedImg;

  const goHome = () => {
    navigate("/", { replace: true });
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="http-error-container">
      <img
        className="error-illustration"
        src={safeImgSrc}
        alt="Error illustration"
      />
      <div className="error-separator" />

      <div className="http-error-header">{heading}</div>
      <div className="http-error-sub-header">{message}</div>

      <div className="error-actions">
        {(status === 404 || status === 400) && (
          <div className="error-btn" onClick={goHome}>
            <MotifIcon
              fill="#5F5F72"
              iconFunction={IconoirArrowLeft}
              size="24"
            />
            <div className="back-btn">Home</div>
          </div>
        )}

        {status === 500 && (
          <div className="error-btn" onClick={goBack}>
            <div className="back-btn">Reload</div>
            <MotifIcon fill="#5F5F72" iconFunction={IconoirRefresh} size="24" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
